import React, { useEffect, useState } from 'react'
import { Table, Row, Col } from 'react-bootstrap'
import axios from 'axios'
import moment from 'moment-timezone'
import PaginationComponent from '../Pagination/Pagination'
import SpinnerComponents from '../Spinner/SpinnerComponents'
import { Link } from 'react-router-dom'
import './TVSchedule.css'

const TVSchedule = () => {
  const [schedule, setSchedule] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [currentDate, setCurrentDate] = useState(moment.tz('Europe/London'))
  const itemsPerPage = 20

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const response = await axios.get('https://nastaanaisie.pythonanywhere.com/api/current-schedule/')
        setSchedule(response.data.results || [])
        setLoading(false)
      } catch (error) {
        console.error('Error fetching the schedule:', error)
        setLoading(false)
      }
    }

    fetchSchedule()
    
    // Update current date every minute (UK time - GMT/BST)
    const interval = setInterval(() => {
      setCurrentDate(moment.tz('Europe/London'))
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  // Format time to UK timezone (GMT/BST)
  const formatUKTime = (timeString) => {
    if (!timeString) return 'N/A'
    return moment.tz(timeString, 'Europe/London').format('HH:mm')
  }

  // Format date to UK timezone
  const formatUKDate = (dateString) => {
    if (!dateString) return 'N/A'
    return moment.tz(dateString, 'Europe/London').format('dddd, MMMM Do YYYY')
  }

  // Get day name
  const getDayName = (dateString) => {
    if (!dateString) return 'N/A'
    return moment.tz(dateString, 'Europe/London').format('dddd')
  }

  // Calculate pagination
  const totalItems = schedule.length
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 0
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = schedule.length > 0 ? schedule.slice(indexOfFirstItem, indexOfLastItem) : []

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Group schedule by date (UK timezone)
  const groupedSchedule = currentItems.reduce((acc, item) => {
    if (!item.start_time) return acc
    const date = moment.tz(item.start_time, 'Europe/London').format('YYYY-MM-DD')
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date].push(item)
    return acc
  }, {})

  return (
    <div className="schedule-container">
      {/* Page Header */}
      <div className="schedule-header">
        <h6 className="schedule-subtitle">TV Guide</h6>
        <h1 className="schedule-title">TV Schedule</h1>
        <p className="schedule-description">
          View our current programming schedule. All times shown in UK time (GMT/BST).
        </p>
        <div className="current-time">
          <span className="time-label">Current UK Time:</span>
          <span className="time-value">{currentDate.format('dddd, MMMM Do YYYY, HH:mm')}</span>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="schedule-loading">
          <SpinnerComponents />
        </div>
      ) : (
        <>
          {/* Schedule Table */}
          {currentItems.length > 0 ? (
            <>
              <div className="schedule-table-wrapper">
                <Table responsive className="schedule-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Show Title</th>
                      <th>Date</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedSchedule).map(([date, items]) => (
                      <React.Fragment key={date}>
                        <tr className="date-header-row">
                          <td colSpan="4" className="date-header">
                            {moment.tz(date, 'Europe/London').format('dddd, MMMM Do YYYY')}
                          </td>
                        </tr>
                        {items.map((item) => {
                          const startTime = moment.tz(item.start_time, 'Europe/London')
                          const endTime = moment.tz(item.end_time, 'Europe/London')
                          const duration = moment.duration(endTime.diff(startTime))
                          const durationHours = Math.floor(duration.asHours())
                          const durationMinutes = duration.minutes()
                          
                          return (
                            <tr key={item.id} className="schedule-row">
                              <td className="time-cell">
                                <span className="time-start">{formatUKTime(item.start_time)}</span>
                                <span className="time-separator">-</span>
                                <span className="time-end">{formatUKTime(item.end_time)}</span>
                              </td>
                              <td className="show-title-cell">
                                {item.show && item.show.title ? item.show.title : 'TBA'}
                              </td>
                              <td className="date-cell">
                                {getDayName(item.start_time)}
                              </td>
                              <td className="duration-cell">
                                {durationHours > 0 ? `${durationHours}h ` : ''}
                                {durationMinutes > 0 ? `${durationMinutes}m` : ''}
                              </td>
                            </tr>
                          )
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="schedule-pagination">
                  <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}

              {/* View All Link */}
              <div className="view-all-schedules">
                <Link to="/allschedules" className="view-all-link">
                  View All Schedules →
                </Link>
              </div>
            </>
          ) : (
            <div className="schedule-empty">
              <p>No schedule available at the moment.</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default TVSchedule
